import type { APIErrorType } from "@app/types/error";
import { isAPIError, isAPIErrorResponse } from "@app/types/error";
import type { NotificationType } from "@dust-tt/sparkle";
import type { MessageDescriptor } from "@lingui/core";
import { msg, t } from "@lingui/core/macro";

const UNEXPECTED_ERROR_MESSAGE = msg`An unexpected error occurred.`;

/**
 * @cc [owner:Nils-Fedrigo,label:error-handling;react] api-error-type-has-message
 * Every `APIErrorType` MUST map to a user-facing message describing the error on its own, without
 * relying on the server `message`. Adding a type to `API_ERROR_TYPES` without a message here MUST
 * fail typecheck.
 */
export const API_ERROR_MESSAGES: Record<APIErrorType, MessageDescriptor> = {
  not_authenticated: msg`You are not signed in.`,
  sso_enforced: msg`This workspace requires you to sign in with SSO.`,
  missing_authorization_header_error: msg`The request is missing its authorization header.`,
  malformed_authorization_header_error: msg`The request authorization header is malformed.`,
  invalid_basic_authorization_error: msg`The request basic authorization is invalid.`,
  invalid_oauth_token_error: msg`Your session token is invalid.`,
  expired_oauth_token_error: msg`Your session has expired. Sign in again.`,
  invalid_api_key_error: msg`The API key is invalid.`,
  invalid_sandbox_token_error: msg`The sandbox token is invalid.`,
  internal_server_error: msg`Something went wrong on our side.`,
  invalid_request_error: msg`The request is malformed.`,
  invalid_rows_request_error: msg`The table rows are invalid.`,
  user_not_found: msg`User not found.`,
  content_too_large: msg`The content is too large.`,
  data_source_error: msg`The data source returned an error.`,
  data_source_not_found: msg`Data source not found.`,
  data_source_view_not_found: msg`Data source view not found.`,
  data_source_auth_error: msg`You don't have access to this data source.`,
  data_source_quota_error: msg`The data source has reached its quota.`,
  workspace_quota_error: msg`The workspace has reached its quota.`,
  data_source_document_not_found: msg`Document not found.`,
  data_source_not_managed: msg`This data source isn't managed by a connection.`,
  run_error: msg`The run failed.`,
  app_not_found: msg`App not found.`,
  app_auth_error: msg`You don't have access to this app.`,
  provider_auth_error: msg`The model provider rejected the credentials.`,
  provider_not_found: msg`Model provider not found.`,
  provider_endpoint_error: msg`The model provider endpoint is invalid or can't be reached.`,
  dataset_not_found: msg`Dataset not found.`,
  workspace_not_found: msg`Workspace not found.`,
  workspace_in_different_cell: msg`This workspace is hosted in another region.`,
  workspace_auth_error: msg`You don't have access to this workspace.`,
  workspace_can_use_product_required_error: msg`Your workspace can't use this product with its current plan.`,
  workspace_user_not_found: msg`User not found in this workspace.`,
  method_not_supported_error: msg`This operation isn't supported.`,
  personal_workspace_not_found: msg`Personal workspace not found.`,
  action_unknown_error: msg`The action failed with an unknown error.`,
  action_api_error: msg`The action failed.`,
  membership_not_found: msg`Membership not found.`,
  membership_revoked: msg`Your membership to this workspace was revoked.`,
  invitation_not_found: msg`Invitation not found.`,
  plan_limit_error: msg`You've reached a limit of your plan.`,
  template_not_found: msg`Template not found.`,
  chat_message_not_found: msg`Message not found.`,
  connector_not_found_error: msg`Connection not found.`,
  connector_update_error: msg`The connection couldn't be updated.`,
  connector_update_unauthorized: msg`You aren't allowed to update this connection.`,
  connector_oauth_connection_not_found: msg`The connection's authorization wasn't found.`,
  connector_oauth_target_mismatch: msg`The authorized account doesn't match this connection.`,
  connector_oauth_user_missing_rights: msg`The authorized account is missing required permissions.`,
  connector_oauth_user_must_be_admin: msg`The authorized account must be an admin.`,
  connector_provider_not_supported: msg`This connection type isn't supported.`,
  connector_credentials_error: msg`The connection credentials are invalid.`,
  connector_credentials_not_found: msg`The connection credentials weren't found.`,
  connector_operation_in_progress: msg`Another operation is in progress on this connection.`,
  agent_configuration_not_found: msg`Agent not found.`,
  agent_inaccessible: msg`You don't have access to this agent.`,
  agent_group_permission_error: msg`You don't have permission to edit this agent.`,
  agent_message_error: msg`The agent message failed.`,
  unprocessable_entity: msg`The request couldn't be processed.`,
  message_not_found: msg`Message not found.`,
  plan_message_limit_exceeded: msg`You've reached the message limit of your plan.`,
  credits_exhausted: msg`Your workspace has run out of credits.`,
  user_cap_reached: msg`You've reached your usage limit.`,
  group_shared_usage_limit_reached: msg`Your group has reached its shared usage limit.`,
  no_seat: msg`You don't have a seat in this workspace.`,
  model_disabled: msg`This model is disabled.`,
  global_agent_error: msg`This Dust agent couldn't be updated.`,
  stripe_invalid_product_id_error: msg`The billing product is invalid.`,
  rate_limit_error: msg`Too many requests. Try again in a moment.`,
  subscription_payment_failed: msg`The subscription payment failed.`,
  subscription_not_found: msg`Subscription not found.`,
  subscription_state_invalid: msg`The subscription is in an invalid state.`,
  trial_restriction: msg`This isn't available during the trial.`,
  service_unavailable: msg`The service is temporarily unavailable.`,
  feature_not_enabled: msg`This feature isn't enabled for your workspace.`,
  project_space_required: msg`This is only available in Pods.`,
  assistant_saving_error: msg`The agent couldn't be saved.`,
  unexpected_error_format: msg`The server returned an unexpected error.`,
  unexpected_response_format: msg`The server returned an unexpected response.`,
  unexpected_network_error: msg`A network error occurred.`,
  action_failed: msg`The action failed.`,
  unexpected_action_response: msg`The action returned an unexpected response.`,
  feature_flag_not_found: msg`Feature flag not found.`,
  feature_flag_already_exists: msg`This feature flag already exists.`,
  invalid_pagination_parameters: msg`The pagination parameters are invalid.`,
  table_not_found: msg`Table not found.`,
  invitation_already_sent_recently: msg`An invitation was already sent recently.`,
  dust_app_secret_not_found: msg`Secret not found.`,
  key_not_found: msg`API key not found.`,
  insufficient_key_scope: msg`The API key doesn't have the required scope.`,
  admin_key_analytics_groups_not_allowed: msg`Admin API keys already read the analytics of every group.`,
  analytics_group_kind_not_supported: msg`Analytics access can only be granted on manual or provisioned groups.`,
  transcripts_configuration_not_found: msg`Transcripts configuration not found.`,
  transcripts_configuration_default_not_allowed: msg`This transcripts configuration can't be the default.`,
  transcripts_configuration_already_exists: msg`A transcripts configuration already exists.`,
  file_not_found: msg`File not found.`,
  file_too_large: msg`The file is too large.`,
  file_type_not_supported: msg`This file type isn't supported.`,
  file_is_empty: msg`The file is empty.`,
  file_read_only: msg`You can only read this file.`,
  collab_session_conflict: msg`This file is being edited live and changed before your edit could be saved. Reload it and try again.`,
  collab_session_write_refused: msg`This file is being edited live and can't take this change.`,
  collab_session_unsupported_location: msg`Only files in a conversation or a Pod can be edited live.`,
  run_not_found: msg`Run not found.`,
  space_already_exists: msg`A space with this name already exists.`,
  space_not_found: msg`Space not found.`,
  project_task_not_found: msg`Task not found.`,
  group_not_found: msg`Group not found.`,
  invalid_shared_usage_limit_order: msg`The order sent does not list every group with a budget exactly once.`,
  shared_usage_limit_order_changed: msg`The order of group budgets changed since you opened it. Reopen the group to see the current order.`,
  coupon_not_found: msg`Coupon not found.`,
  coupon_not_redeemable: msg`This coupon can't be redeemed.`,
  coupon_already_redeemed: msg`This coupon was already redeemed.`,
  plugin_not_found: msg`Plugin not found.`,
  plugin_execution_failed: msg`The plugin failed.`,
  recommendation_not_found: msg`Recommendation not found.`,
  activation_work_area_not_found: msg`Work area not found.`,
  trigger_not_found: msg`Trigger not found.`,
  webhook_source_not_found: msg`Webhook source not found.`,
  webhook_source_view_auth_error: msg`You don't have access to this webhook source.`,
  webhook_source_auth_error: msg`You don't have access to this webhook source.`,
  webhook_source_view_not_found: msg`Webhook source not found.`,
  webhook_source_view_triggering_agent: msg`This webhook source is used by an agent trigger.`,
  webhook_source_misconfiguration: msg`The webhook source is misconfigured.`,
  webhook_processing_error: msg`The webhook couldn't be processed.`,
  webhook_storage_error: msg`The webhook couldn't be stored.`,
  mcp_server_connection_not_found: msg`Tool connection not found.`,
  mcp_server_view_not_found: msg`Tool not found.`,
  action_not_found: msg`Action not found.`,
  action_not_blocked: msg`This action isn't waiting for approval.`,
  conversation_not_found: msg`Conversation not found.`,
  conversation_access_restricted: msg`You don't have access to this conversation.`,
  conversation_agent_running: msg`An agent is still running in this conversation.`,
  conversation_with_unavailable_agent: msg`This conversation uses an agent that is no longer available.`,
  user_already_participant: msg`You're already a participant in this conversation.`,
  message_deletion_not_authorized: msg`You aren't allowed to delete this message.`,
  message_outdated: msg`This message has changed. Refresh the page and try again.`,
  conversation_context_usage_not_found: msg`Conversation context usage not found.`,
  mcp_auth_error: msg`The tool rejected the credentials.`,
  personal_tools_unavailable: msg`These tools use each person's own credentials, so they can't be used for a message Dust posted.`,
  invalid_mcp_server_id: msg`The tool identifier is invalid.`,
  mcp_server_not_found: msg`Tool not found.`,
  workos_organization_not_found: msg`Organization not found.`,
  workos_server_error: msg`The authentication provider returned an error.`,
  workos_multiple_sso_connections_not_supported: msg`Multiple SSO connections aren't supported.`,
  workos_multiple_directories_not_supported: msg`Multiple directories aren't supported.`,
  user_authentication_required: msg`You need to sign in to continue.`,
  agent_memory_not_found: msg`Agent memory not found.`,
  elasticsearch_error: msg`Search failed.`,
  skill_not_found: msg`Skill not found.`,
  skill_github_repository_not_found: msg`GitHub repository not found.`,
  sandbox_function_not_found: msg`Sandbox function not found.`,
  sandbox_function_invocation_not_found: msg`Sandbox function call not found.`,
  frame_runtime_unavailable: msg`The frame runtime is unavailable.`,
  frame_manifest_not_movable: msg`A Frame's manifest can't be moved or renamed on its own. Move or rename the Frame's folder instead.`,
  fast_function_called_tools: msg`A fast function can't call tools.`,
  approval_required_unattended: msg`This tool needs approval, which a scheduled run can't wait for.`,
  personal_auth_required_unattended: msg`This tool needs a personal connection, which a scheduled run can't set up.`,
  project_metadata_not_found: msg`Project details not found.`,
  agent_suggestion_not_found: msg`Suggestion not found.`,
  batch_suggestion_not_found: msg`Suggestions not found.`,
  wakeup_not_found: msg`Wake-up not found.`,
  conversation_locked: msg`Another user has an active wake-up in this conversation. Only they can post until it fires or is canceled.`,
  open_pods_disabled: msg`Open Pods are disabled in this workspace. Make the Pod private instead.`,
  space_group_kind_not_supported: msg`Only provisioned and manual groups can be given access to a space.`,
  global_space_cannot_be_restricted: msg`The company space can't be restricted.`,
  global_space_not_renamable: msg`The company space can't be renamed.`,
  space_in_use: msg`This space has data sources or apps used by agents or skills.`,
  already_pod_member: msg`You're already a member of this Pod.`,
  user_already_space_member: msg`This user is already a member of the space.`,
  system_group_members_not_editable: msg`Users can't be added to or removed from system or global groups.`,
  space_kind_has_no_members: msg`This kind of space can't have members.`,
  data_source_provider_not_allowed_in_space: msg`This connection type can't be added to this space.`,
  embedding_api_key_missing: msg`An OpenAI API key is required. Configure it in your workspace settings or contact an admin.`,
  data_source_already_exists: msg`A data source with this name already exists.`,
  data_source_name_not_allowed: msg`Data source names can't be empty or start with "managed-".`,
  connector_creation_failed: msg`The connection couldn't be created.`,
  managed_data_source_not_editable: msg`Data sources managed by a connection can't be edited.`,
  data_source_not_deletable: msg`This data source can't be deleted.`,
  data_source_view_already_exists: msg`This data source is already in this space.`,
  search_query_too_short: msg`The search query is too short.`,
  no_searchable_space: msg`You don't have access to any space to search.`,
  app_name_invalid: msg`App names must be 1 to 64 characters long and use only letters, numbers, underscores and dashes.`,
  dataset_already_exists: msg`A dataset with this name already exists in this app.`,
  dataset_name_invalid: msg`Dataset names can only use letters, numbers and underscores.`,
  dataset_data_invalid: msg`The dataset data is invalid. Check that every entry matches the schema.`,
  pod_file_tab_limit_exceeded: msg`This Pod has reached the maximum number of file tabs.`,
  pod_file_tab_not_previewable: msg`This file can't be opened in a tab.`,
  assignee_not_pod_member: msg`The assignee must be a member of this Pod.`,
  pod_tasks_already_seeded: msg`This Pod already has its starter tasks.`,
  notion_connector_required: msg`This is only available for Notion connections.`,
  google_drive_connection_not_found: msg`No Google Drive connection found. Connect your Google Drive account first.`,
  project_task_not_startable: msg`Only tasks in "Need to do" can be started.`,
  data_source_file_use_case_not_supported: msg`This file can't be added to a data source.`,
  data_source_file_not_ready: msg`The file isn't ready yet. Try again in a moment.`,
  table_file_content_invalid: msg`The file content can't be read as a table.`,
  node_title_too_long: msg`The title is too long.`,
  app_in_use: msg`This app is used by agents.`,
  message_type_not_supported: msg`This isn't available for this type of message.`,
  message_author_required: msg`Only the person who sent this message can do this.`,
  mention_not_found: msg`Mention not found.`,
  mention_user_not_added_to_pod: msg`This person couldn't be added to the Pod.`,
  conversation_busy: msg`An agent or a compaction is running in this conversation.`,
  conversation_compaction_running: msg`This conversation is being compacted. Try again once it's done.`,
  conversation_already_compacted: msg`This conversation was just compacted. Send a new message before compacting again.`,
  conversation_fork_pending: msg`This conversation is still being set up. Try again in a moment.`,
  multiple_agent_mentions: msg`You can only mention one agent per message.`,
  model_not_supported: msg`This model isn't supported.`,
  fork_source_message_not_found: msg`There's no completed agent answer to branch from.`,
  conversation_move_not_supported: msg`This conversation can't be moved into or out of this Pod yet.`,
  conversation_sharing_not_supported_in_pod: msg`Pod conversations are shared with the Pod's members.`,
  conversation_sharing_unavailable: msg`This conversation can't be shared yet.`,
  space_selection_not_supported_in_pod: msg`Spaces can't be selected in a Pod conversation.`,
  space_not_selectable: msg`Only regular spaces can be selected.`,
  mcp_server_restricted_to_skills: msg`This tool can only be used through skills.`,
  mcp_server_access_denied: msg`You don't have access to one of the selected tools.`,
  action_not_editable: msg`These action inputs can't be edited.`,
  agent_message_not_resumable: msg`This agent message can't be resumed.`,
  invalid_webhook_signature: msg`The webhook signature is invalid.`,
  file_not_attachable: msg`This file can't be attached to a conversation.`,
  file_not_ready: msg`The file isn't ready. Upload it again.`,
  content_node_not_found: msg`The attached content wasn't found.`,
  attachment_space_not_allowed: msg`In a Pod, you can only attach content from the Pod or from Company Data.`,
  content_fragment_not_found: msg`Attachment not found.`,
  agent_archived: msg`This agent is archived. Restore it first.`,
  active_custom_agent_required: msg`This is only available for active custom agents.`,
  agent_already_exists: msg`An active agent with this name already exists.`,
  agent_already_created: msg`This agent has already been created.`,
  agent_modified_concurrently: msg`This agent was changed at the same time. Try again.`,
  agent_editor_required: msg`An agent needs at least one editor.`,
  user_already_agent_editor: msg`This user is already an editor of the agent.`,
  user_not_agent_editor: msg`This user isn't an editor of the agent.`,
  editor_not_workspace_member: msg`Editors must be active members of the workspace.`,
  conflicting_suggestions: msg`These suggestions can't be applied together.`,
  agent_suggestion_not_applicable: msg`This suggestion no longer applies to the agent.`,
  agent_suggestion_not_supported: msg`This kind of suggestion can't be applied.`,
  suggestion_batch_already_reviewed: msg`These suggestions have already been reviewed.`,
  sidekick_suggestion_required: msg`Only Sidekick suggestions can be reviewed here.`,
  model_not_available: msg`This model isn't available in your workspace.`,
  no_model_available: msg`No model is available in your workspace.`,
  reasoning_effort_not_supported: msg`This model doesn't support the selected reasoning effort.`,
  structured_output_not_supported: msg`This model doesn't support structured output.`,
  invalid_response_format: msg`The structured output format is invalid.`,
  tag_not_found: msg`Tag not found.`,
  tag_not_on_agent: msg`This tag isn't on the agent.`,
  tag_already_on_agent: msg`This tag is already on the agent.`,
  tag_change_not_allowed: msg`You aren't allowed to add or remove these tags.`,
  homepage_use_case_not_found: msg`Use case not found.`,
  homepage_use_case_not_dismissible: msg`This use case can't be dismissed.`,
  discovery_pin_target_not_found: msg`Agent or skill not found.`,
  discovery_pin_target_not_readable_by_group: msg`This group doesn't have access to this agent or skill.`,
  discovery_pin_group_not_supported: msg`Items can't be pinned for this group.`,
  skill_archived: msg`This skill is archived. Restore it to change it.`,
  skill_not_active: msg`This skill isn't active.`,
  skill_not_custom: msg`Only custom skills can be changed.`,
  skill_already_exists: msg`A skill with this name already exists.`,
  skill_name_empty: msg`Enter a name for the skill.`,
  skill_name_too_long: msg`This skill name is too long. Use a shorter name.`,
  skill_editors_missing_space_access: msg`Some editors don't have access to the spaces this skill uses. Add them to those spaces or remove them from the editors.`,
  skill_last_editor_removed: msg`A skill needs at least one editor.`,
  skill_attachment_file_invalid: msg`This file can't be attached to a skill.`,
  skill_file_not_attached: msg`This file isn't attached to the skill.`,
  skill_file_already_exists: msg`The skill already has a file with this name.`,
  skill_instructions_reference_unavailable: msg`The instructions use tools or knowledge you don't have access to.`,
  skill_already_created: msg`This skill has already been created.`,
  skill_suggestion_invalid: msg`This suggestion can't be applied to the skill.`,
  skill_suggestion_outdated: msg`This suggestion no longer matches the skill's instructions.`,
  skill_change_not_authorized: msg`You don't have permission to make this change to the skill.`,
  skill_suggestion_not_reviewable: msg`This suggestion can't be reviewed here.`,
  skill_suggestion_batch_incomplete: msg`Suggestions from the same batch must be reviewed together.`,
  skills_not_detected: msg`No skills found. A skill needs a SKILL.md file with valid frontmatter.`,
  skill_archive_invalid: msg`This ZIP archive can't be read or is too large.`,
  skill_import_invalid: msg`Some skills can't be imported. Check their names and whether they already exist.`,
  skill_import_not_allowed: msg`You don't have permission to import these skills.`,
  skill_github_repository_url_invalid: msg`This GitHub repository URL isn't valid.`,
  skill_github_auth_error: msg`GitHub denied access to this repository.`,
  skill_github_api_error: msg`GitHub returned an error. Try again later.`,
  skill_github_connection_invalid: msg`The GitHub connection is invalid. Connect GitHub again.`,
  sandbox_token_not_allowed: msg`This sandbox token can't access this endpoint.`,
  file_not_frame: msg`This file isn't a Frame.`,
  frame_not_shareable: msg`This Frame isn't shareable, so it can't be exported.`,
  file_not_mounted: msg`This file can't be moved to a Pod.`,
  branding_asset_mismatch: msg`This file doesn't match the branding asset.`,
  frame_text_edit_failed: msg`The Frame text couldn't be updated.`,
  frame_draft_conflict: msg`This Frame changed or is being updated. Refresh it and try again.`,
  frame_discard_unavailable: msg`This draft can't be discarded.`,
  frame_discard_incomplete: msg`The draft was only partly discarded. Discard it again to finish.`,
  frame_not_published: msg`This Frame has no live version yet.`,
  frame_make_live_failed: msg`These changes couldn't be made live.`,
  frame_not_in_conversation: msg`This Frame isn't in this conversation's files.`,
  file_referenced_by_skill: msg`This file is used by a skill and can't be deleted.`,
  file_deletion_failed: msg`The file couldn't be deleted.`,
  file_not_in_conversation: msg`Only files from a conversation can be saved to a Pod.`,
  frame_sharing_not_allowed: msg`This Frame can't be shared this way.`,
  frame_files_unverifiable: msg`This Frame uses files that can't be verified.`,
  frame_user_required: msg`This Frame must be created or updated by a user signed in to Dust.`,
  frame_app_creation_not_allowed: msg`You can't create or update Frame Apps in this workspace.`,
  file_already_exists: msg`A file or folder with this name already exists.`,
  folder_required: msg`Only folders can be downloaded as a ZIP.`,
  folder_too_large_to_download: msg`This folder is too large to download.`,
  archive_too_large: msg`The archive is too large.`,
  archive_invalid: msg`This archive is invalid or contains unsafe paths.`,
  file_revision_conflict: msg`This file changed since you opened it. Reload it and try again.`,
  file_revision_not_supported: msg`This file doesn't support conditional updates.`,
  comment_unwritable: msg`This comment can't be saved as written.`,
  file_comments_changed: msg`Comments in this file changed. Reload the file before saving.`,
  frame_manifest_invalid: msg`The Frame's manifest is invalid.`,
  sandbox_function_invocation_initiator_required: msg`Only the user who started this function call can do this.`,
  egress_domain_invalid: msg`This domain isn't valid. Use a domain like api.github.com or *.github.com.`,
  sandbox_env_var_invalid: msg`The environment variable name or value isn't valid.`,
  sandbox_env_var_not_found: msg`Environment variable not found.`,
  sandbox_env_var_demotion_not_supported: msg`An HTTPS secret can't be turned back into a config variable.`,
  file_name_invalid: msg`File names can't be empty or contain slashes.`,
  file_already_uploaded: msg`This file was already uploaded or its upload failed.`,
  file_upload_expired: msg`The upload has expired. Upload the file again.`,
  file_url_fetch_failed: msg`The file couldn't be downloaded from its URL.`,
  file_url_not_allowed: msg`This URL can't be used to download a file.`,
  metronome_billing_not_configured: msg`This workspace isn't set up for credit-based billing.`,
  credit_priced_plan_required: msg`This is only available on credit-based plans.`,
  usage_limit_out_of_range: msg`The limit is outside the allowed range.`,
  billing_period_unavailable: msg`The current billing period couldn't be found.`,
  api_key_name_required: msg`Enter a name for the API key.`,
  api_key_already_exists: msg`An API key with this name already exists.`,
  system_key_spend_limit_not_supported: msg`System keys can't have a spend limit.`,
  invalid_email_address: msg`One or more email addresses are invalid.`,
  pending_invitations_limit_exceeded: msg`There are too many pending invitations. Ask members to accept theirs before sending more.`,
  invitation_token_invalid: msg`This invitation link is invalid. Ask your admin to send a new invitation.`,
  upgrade_request_reason_required: msg`Add a reason to request an upgrade.`,
  upgrade_request_not_found: msg`Upgrade request not found.`,
  upgrade_request_already_resolved: msg`This upgrade request was already approved or denied.`,
  user_already_has_workspace: msg`You already have a workspace.`,
  user_name_required: msg`Enter your first and last name.`,
  provisioned_user_name_not_editable: msg`Your name is managed by your identity provider and can't be changed here.`,
  group_already_exists: msg`A group with this name already exists.`,
  group_kind_not_supported: msg`This is only available for manual or provisioned groups.`,
  group_not_editable: msg`This group is managed automatically and can't be edited.`,
  group_requirements_not_met: msg`This change would leave the group without a required member.`,
  user_already_group_member: msg`This user is already a member of the group.`,
  user_not_group_member: msg`This user isn't a member of the group.`,
  last_group_member_cannot_be_removed: msg`A group must keep at least one member. To remove everyone, delete the group.`,
  no_matching_members: msg`No active members match the selection.`,
  last_admin_cannot_be_removed: msg`A workspace must keep at least one admin.`,
  free_seat_already_used: msg`The free seat is only for first-time members and can't be assigned again.`,
  paid_seat_requires_paid_plan: msg`Upgrade your workspace before assigning a paid seat.`,
  seat_limit_reached: msg`This seat type has reached its limit for your workspace.`,
  seat_type_not_in_contract: msg`Your workspace contract doesn't include this seat type.`,
  seat_change_preview_unavailable: msg`The seat change preview couldn't be computed.`,
  subscription_cancellation_scheduled: msg`Seats can't be changed while a subscription cancellation is scheduled.`,
  already_on_paid_plan: msg`Your workspace is already on a Pro or Business plan.`,
  free_plan_required: msg`Your workspace must be on the free plan to activate this subscription.`,
  payment_setup_incomplete: msg`Payment setup hasn't completed.`,
  enterprise_credit_purchase_not_supported: msg`Enterprise workspaces can't buy credits here. Contact your Dust sales representative.`,
  stripe_customer_not_found: msg`No billing account was found for this workspace. Contact support.`,
  credit_purchase_pending: msg`A credit purchase is already in progress. Wait for it to complete.`,
  credit_purchase_amount_out_of_range: msg`The credit amount is outside the allowed range.`,
  workspace_phone_not_verified: msg`Verify your workspace's phone number before starting a trial.`,
  phone_number_not_accepted: msg`This phone number can't be used for verification.`,
  phone_already_used_error: msg`This phone number is already used by another workspace.`,
  verification_error: msg`The phone number couldn't be verified. Try again or request a new code.`,
  invalid_captcha: msg`The captcha check failed. Try again.`,
  user_memory_too_long: msg`This memory is too long. Shorten it and try again.`,
  tag_already_exists: msg`A tag with this name already exists.`,
  verified_domain_not_found: msg`Verified domain not found.`,
  data_classification_source_not_supported: msg`Data classification labels aren't supported for this source.`,
  non_regional_models_in_use: msg`Some active agents use models outside your region. Update them first.`,
  redirect_uri_invalid: msg`Each redirect URI must be unique and include a scheme, such as https://.`,
  trigger_status_managed_by_dust: msg`This trigger's status is managed by Dust and can't be changed.`,
  bulk_automation_limit_exceeded: msg`Too many automations are selected. Select fewer and try again.`,
  invalid_trigger_filter: msg`The filter expression is invalid. Check its syntax.`,
  webhook_source_already_exists: msg`A webhook source with this name already exists.`,
  webhook_subscribed_events_required: msg`Select at least one event.`,
  regular_or_global_space_required: msg`This is only available in the company space and regular spaces.`,
  system_view_required: msg`This can only be updated from the workspace settings.`,
  mcp_server_view_already_exists: msg`A tool with this name already exists.`,
  auto_tool_not_removable: msg`Auto tools can't be removed from the company space.`,
  mcp_server_name_invalid: msg`The tool name is empty or too long.`,
  mcp_server_url_too_long: msg`The tool's URL is too long.`,
  invalid_mcp_server_url: msg`The tool's URL is invalid or not allowed. Use a public HTTPS URL.`,
  remote_mcp_server_connection_failed: msg`Couldn't connect to the tool's server. Check its URL and credentials.`,
  mcp_oauth_connection_error: msg`The tool's authorization couldn't be used. Reconnect it and try again.`,
  mcp_server_not_available: msg`This tool isn't available for your workspace.`,
  mcp_server_already_installed: msg`This tool is already added, and only one instance is allowed.`,
  mcp_server_icon_not_editable: msg`This tool's icon can't be changed.`,
  mcp_server_bearer_token_not_supported: msg`This tool doesn't support bearer token credentials.`,
  mcp_credential_connection_not_supported: msg`This tool can't be connected with stored credentials.`,
  credential_provider_mismatch: msg`These credentials are for a different service.`,
  bigquery_project_too_large: msg`This BigQuery project is too large to check automatically. Contact support to connect it.`,
  bigquery_locations_check_failed: msg`Couldn't list the BigQuery project's locations. Check the credentials and their permissions.`,
};

export type FormattedError = {
  /** User-facing description of what failed. */
  description: string;
  /** Raw error code and untranslated message, shown under the collapsed "Details" of a toast. */
  details?: string;
};

function getRawDetails(error: unknown): string | undefined {
  const lines: string[] = [];
  if (typeof error === "object" && error !== null) {
    if ("type" in error && typeof error.type === "string") {
      const code = error.type;
      lines.push(t`Code: ${code}`);
    }
    if ("message" in error && typeof error.message === "string") {
      const rawMessage = error.message;
      lines.push(t`Message: ${rawMessage}`);
    }
  }
  return lines.length > 0 ? lines.join("\n") : undefined;
}

/**
 * @cc [owner:Nils-Fedrigo,label:error-handling;react] format-error-api-errors
 * For an API error, or an `APIErrorResponse` wrapping one, `description` MUST be the translation of
 * its `type` from `API_ERROR_MESSAGES`, and `details` MUST hold its raw `type` and untranslated
 * `message`. The server `message` MUST NOT appear in `description`.
 */
/**
 * @cc [owner:Nils-Fedrigo,label:error-handling;react] format-error-other-values
 * For any other value (network `TypeError`, non-API `Error`, connectors error, unknown `type`,
 * `undefined`, ...), `description` MUST be the translated `UNEXPECTED_ERROR_MESSAGE`, and `details`
 * holds the value's `type` and `message` when it has them. `formatError` MUST NOT throw.
 */
export function formatError(error: unknown): FormattedError {
  try {
    return formatErrorUnsafe(error);
  } catch {
    // Inspecting `error` can throw (e.g. a throwing getter): fall back rather than fail the caller.
    return { description: t(UNEXPECTED_ERROR_MESSAGE) };
  }
}

function formatErrorUnsafe(error: unknown): FormattedError {
  const apiError = isAPIErrorResponse(error)
    ? error.error
    : isAPIError(error)
      ? error
      : null;

  return {
    description: apiError
      ? t(API_ERROR_MESSAGES[apiError.type])
      : t(UNEXPECTED_ERROR_MESSAGE),
    details: getRawDetails(apiError ?? error),
  };
}

/**
 * @cc [owner:Nils-Fedrigo,label:error-handling;react] error-notification-shape
 * Returns an `error` notification whose `title` is the given title (the action that failed) and
 * whose `description` and `details` are those of `formatError(error)`.
 */
export function errorNotification(
  title: string,
  error: unknown
): NotificationType {
  return { type: "error", title, ...formatError(error) };
}
